import type { Country } from '@prisma/client';
import {
  type OwnershipVariant,
  type PartnershipStructure,
  VARIANT_COPY,
} from './types';

export const AUTHORIZED_SHARES_DESCRIPTION_DEFAULT =
  "Authorized shares are the maximum number of shares your corporation is legally allowed to issue. You don't have to issue them all upfront — most companies issue only a portion and hold the rest in reserve. We recommend 10,000,000 as the default. You can always authorize more later by amending your charter, but starting at 10M avoids that hassle.";

const INDIA: Country = 'IN';

// Whether the structure designates a General Partner. Unset means the legacy
// default (`gp_and_lp`); `lp_only` (e.g. Indian LLP) has no GP role at all.
export const structureHasGeneralPartner = (
  structure: PartnershipStructure | undefined
): boolean => (structure ?? 'gp_and_lp') === 'gp_and_lp';

// Reads the structure off an untyped Ownership node `props` bag.
export const readPartnershipStructure = (
  props: Record<string, unknown> | undefined
): PartnershipStructure | undefined => {
  const s = props?.partnershipStructure;
  return s === 'gp_and_lp' || s === 'lp_only' ? s : undefined;
};

export const readEntityDetailsKeyId = (
  props: Record<string, unknown> | undefined
): string | undefined => {
  const keyId = props?.entityDetailsKeyId;
  return typeof keyId === 'string' && keyId ? keyId : undefined;
};

export const partnerRoleLabel = (
  country: string | undefined,
  isGeneralPartner?: boolean
): string => {
  if (isGeneralPartner) return 'General Partner';
  return country === INDIA ? 'Designated Partner' : 'Limited Partner';
};

export const partnerRoleLabelPlural = (country: string | undefined): string =>
  country === INDIA ? 'Designated Partners' : 'Limited Partners';

export const MIN_PARTNERS = 2;
export const MIN_PARTNERS_ERROR = 'A partnership needs at least two partners.';

export const zeroPercentOwnerError = (variant: OwnershipVariant): string =>
  `Every ${VARIANT_COPY[variant].noun} must hold more than 0%.`;

// A required node with an empty cap table would otherwise submit: the integrity
// rules (100%, GP, min capital) only run once someone is staged, and the presence
// rules only cover the capital fields. A partnership has its own stronger rule.
export const noOwnersError = (variant: OwnershipVariant): string =>
  `Add at least one ${VARIANT_COPY[variant].noun}.`;

export const INCOMPLETE_ENTITY_DETAILS_ERROR =
  'Complete the details for every entity owner on the cap table.';

// True when the node collects per-entity details and some entity row is still
// missing them. Gates submit, and surfaces the same message under each table.
export const hasIncompleteEntityDetails = (
  ids: string[],
  entries: Record<string, { owner: { kind: string }; details?: unknown }>,
  collectsDetails: boolean
): boolean =>
  collectsDetails &&
  ids.some((id) => entries[id]?.owner.kind === 'entity' && !entries[id]?.details);
